import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faEdit, 
  faTrash, 
  faFilm,
  faPlay,
  faSortUp,
  faSortDown,
  faStar,
  faList,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import SimpleMovieForm from '../components/SimpleMovieForm';
import '../styles/AdminMovies.css';
import '../styles/SimpleMovieForm.css';

const AdminMovies = () => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMovie, setEditingMovie] = useState(null);

  // API giống trang chủ
  const API_ENDPOINT = 'https://phimapi.com/v1/api/danh-sach/hoat-hinh';
  const APP_DOMAIN_CDN_IMAGE = 'https://phimimg.com/';

  useEffect(() => {
    fetchMoviesData();
  }, []);

  const fetchMoviesData = async () => {
    setLoading(true);
    try {
      // Fetch nhiều trang như trang chủ để có đủ dữ liệu
      const promises = [];
      const maxPages = 8; // Load 8 pages để có đủ phim như trang chủ
      
      for (let page = 1; page <= maxPages; page++) {
        promises.push(
          fetch(`${API_ENDPOINT}?page=${page}&country=nhat-ban`)
            .then(res => res.json())
        );
      }
      
      const responses = await Promise.all(promises);
      let allMovies = [];
      
      responses.forEach((data, pageIndex) => {
        if (data?.data?.items) {
          const pageMovies = data.data.items.map((movie, itemIndex) => {
            // Parse episodes giống trang chủ
            const totalEpisodes = parseInt(movie.episode_total) || 0;
            let currentEpisodes = totalEpisodes;
            
            if (movie.episode_current) {
              const match = movie.episode_current.match(/\d+/);
              if (match) {
                currentEpisodes = parseInt(match[0]);
              }
            }
            
            if (currentEpisodes > totalEpisodes && totalEpisodes > 0) {
              currentEpisodes = totalEpisodes;
            }
            
            return {
              id: movie._id || `${movie.slug}-${pageIndex}-${itemIndex}`,
              name: movie.name,
              origin_name: movie.origin_name,
              slug: movie.slug,
              content: movie.content || 'Chưa có mô tả',
              poster_url: movie.poster_url,
              thumb_url: movie.thumb_url,
              status: currentEpisodes === totalEpisodes && totalEpisodes > 0 ? 'completed' : 'ongoing',
              year: movie.year,
              quality: movie.quality || 'FHD',
              lang: movie.lang || 'Vietsub',
              episode_current: movie.episode_current,
              episode_total: movie.episode_total,
              episodes: currentEpisodes,
              total_episodes: totalEpisodes,
              views: Math.floor(Math.random() * 10000000) + 100000,
              rating: parseFloat((Math.random() * 2 + 8).toFixed(1)),
              created_at: movie.created?.time || new Date().toISOString().split('T')[0],
              updated_at: movie.modified?.time || new Date().toISOString().split('T')[0],
              category: movie.category || [],
              country: movie.country || [],
              // Thêm các field giống trang chủ
              imdb_rating: movie.imdb?.rating,
              tmdb: movie.tmdb
            };
          });
          allMovies = [...allMovies, ...pageMovies];
        }
      });
      
      // Remove duplicates by slug
      const uniqueMovies = allMovies.filter((movie, index, self) => 
        index === self.findIndex(m => m.slug === movie.slug)
      );
      
      console.log(`Loaded ${uniqueMovies.length} movies from ${maxPages} pages (matching homepage data)`);
      
      // Sort by name để có thứ tự đẹp
      uniqueMovies.sort((a, b) => a.name.localeCompare(b.name));
      setMovies(uniqueMovies);
      setFilteredMovies(uniqueMovies);
      
    } catch (error) {
      console.error('Error fetching movies:', error);
      setMovies([]);
      setFilteredMovies([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter và sort với debounce để tránh giật
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      filterAndSortMovies();
    }, 100); // Debounce 100ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, sortField, sortDirection, movies]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterAndSortMovies = () => {
    let filtered = movies.filter(movie => {
      const matchesSearch = movie.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (movie.origin_name && movie.origin_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          movie.content.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || movie.status === filterStatus;
      
      return matchesSearch && matchesStatus;
    });

    // Sort movies
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredMovies(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleEditMovie = (movie) => {
    setEditingMovie(movie);
    setShowAddModal(true);
  };

  const handleDeleteMovie = (movieId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa phim này?')) {
      const updatedMovies = movies.filter(movie => movie.id !== movieId);
      setMovies(updatedMovies);
      setFilteredMovies(filteredMovies.filter(movie => movie.id !== movieId));
    }
  };

  const handleViewEpisodes = (movie) => {
    navigate(`/admin/movies/${movie.slug}/episodes`);
  };

  const handleViewMovie = (movie) => {
    // Open movie detail in new tab
    window.open(`/watch/${movie.slug}`, '_blank');
  };

  const handleSaveMovie = (movieData) => {
    if (editingMovie) {
      const updatedMovies = movies.map(movie => 
        movie.id === editingMovie.id 
          ? { ...movie, ...movieData, updated_at: new Date().toISOString().split('T')[0] }
          : movie
      );
      setMovies(updatedMovies);
    } else {
      const newMovie = {
        id: Date.now(),
        ...movieData,
        views: 0,
        rating: 0,
        created_at: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString().split('T')[0]
      };
      setMovies([newMovie, ...movies]);
    }
    setShowAddModal(false);
    setEditingMovie(null);
  };

  const handleCancelEdit = () => {
    setShowAddModal(false);
    setEditingMovie(null);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSortUp;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  const formatViews = (views) => {
    if (views >= 1000000) {
      return (views / 1000000).toFixed(1) + 'M';
    } else if (views >= 1000) {
      return (views / 1000).toFixed(1) + 'K';
    }
    return views.toString();
  };

  return (
    <div className="admin-movies">
      <div className="movies-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faFilm} />
            Quản lý MAnime
          </h1>
          <p>Quản lý danh sách phim và tập phim trên hệ thống</p>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="add-movie-btn"
        >
          <FontAwesomeIcon icon={faPlus} />
          Thêm phim
        </button>
      </div>

      {/* Stats Cards */}
      <div className="movies-stats">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faFilm} />
          </div>
          <div className="stat-content">
            <div className="stat-label">TỔNG PHIM</div>
            <div className="stat-value">{movies.length}</div>
          </div>
        </div>
        
        <div className="stat-card green">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faPlay} />
          </div>
          <div className="stat-content">
            <div className="stat-label">ĐANG CHIẾU</div>
            <div className="stat-value">{filteredMovies.filter(m => m.status === 'ongoing').length}</div>
          </div>
        </div>
        
        <div className="stat-card orange">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faEye} />
          </div>
          <div className="stat-content">
            <div className="stat-label">TỔNG LƯỢT XEM</div>
            <div className="stat-value">{formatViews(movies.reduce((sum, movie) => sum + movie.views, 0))}</div>
          </div>
        </div>
        
        <div className="stat-card purple">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faStar} />
          </div>
          <div className="stat-content">
            <div className="stat-label">ĐÁNH GIÁ TB</div>
            <div className="stat-value">{movies.length > 0 ? (movies.reduce((sum, movie) => sum + movie.rating, 0) / movies.length).toFixed(1) : '0.0'}</div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="movies-controls">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Tìm kiếm theo tên phim hoặc mô tả..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="ongoing">Đang chiếu</option>
            <option value="completed">Hoàn thành</option>
          </select>
        </div>
      </div>

      {/* Movies Table */}
      <div className="movies-table-container">
        {loading ? (
          <div className="loading-state">
            <FontAwesomeIcon icon={faPlay} spin />
            Đang tải danh sách phim...
          </div>
        ) : (
          <table className="movies-table">
            <thead>
              <tr>
                <th>POSTER</th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('name')}
                >
                  TÊN PHIM
                  <FontAwesomeIcon icon={getSortIcon('name')} />
                </th>
                <th>MÔ TẢ</th>
                <th>TRẠNG THÁI</th>
                <th>TẬP PHIM</th>
                <th>LƯỢT XEM</th>
                <th>ĐÁNH GIÁ</th>
                <th>THAO TÁC</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovies.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    {loading ? 'Đang tải phim...' : 
                     searchTerm || filterStatus !== 'all' 
                      ? 'Không tìm thấy phim nào phù hợp' 
                      : 'Không có phim nào'
                    }
                  </td>
                </tr>
              ) : (
                filteredMovies.map((movie) => (
                  <tr key={movie.id}>
                    <td className="poster-cell">
                      <img 
                        src={movie.poster_url ? `${APP_DOMAIN_CDN_IMAGE}${movie.poster_url}` : '/placeholder-movie.jpg'} 
                        alt={movie.name}
                        className="movie-poster"
                        onError={(e) => {
                          e.target.src = '/placeholder-movie.jpg';
                        }}
                      />
                    </td>
                    <td className="name-cell">
                      <div className="movie-info">
                        <h4 className="movie-title">{movie.name}</h4>
                        {movie.origin_name && (
                          <p className="movie-origin">{movie.origin_name}</p>
                        )}
                        <div className="movie-meta">
                          {movie.year && <span className="year-badge">{movie.year}</span>}
                          <span className="quality-badge">{movie.quality}</span>
                          <span className="lang-badge">VIETSUB</span>
                        </div>
                      </div>
                    </td>
                    <td className="description-cell">
                      <div className="movie-description">
                        {movie.content && movie.content !== 'Chưa có mô tả' ? (
                          movie.content.length > 100 
                            ? movie.content.substring(0, 100) + '...'
                            : movie.content
                        ) : (
                          <span className="no-description">Chưa có mô tả</span>
                        )}
                      </div>
                    </td>
                    <td className="status-cell">
                      <span 
                        className={`status-badge ${movie.status}`}
                        onClick={() => handleViewEpisodes(movie)}
                        title="Click để xem chi tiết tập phim"
                      >
                        {movie.status === 'completed' ? 'HOÀN THÀNH' : 'ĐANG CHIẾU'}
                      </span>
                    </td>
                    <td className="episodes-cell">
                      <span className="episode-count">
                        {movie.episodes}/{movie.total_episodes} tập
                      </span>
                    </td>
                    <td className="views-cell">
                      <span className="views-count">
                        {formatViews(movie.views)}
                      </span>
                    </td>
                    <td className="rating-cell">
                      <div className="rating">
                        <FontAwesomeIcon icon={faStar} className="star-icon" />
                        <span>{movie.rating}</span>
                      </div>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleViewMovie(movie)}
                          className="action-btn view"
                          title="Xem nhanh"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          onClick={() => handleViewEpisodes(movie)}
                          className="action-btn episodes"
                          title="Quản lý tập phim"
                        >
                          <FontAwesomeIcon icon={faList} />
                        </button>
                        <button
                          onClick={() => handleEditMovie(movie)}
                          className="action-btn edit"
                          title="Sửa phim"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteMovie(movie.id)}
                          className="action-btn delete"
                          title="Xóa phim"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Movie Form Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <SimpleMovieForm
              movie={editingMovie}
              onSave={handleSaveMovie}
              onCancel={handleCancelEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMovies;